import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Shield, Eye, EyeOff } from 'lucide-react';
import { useCustomer } from '../../context/CustomerContext';

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function AccountLogin() {
  const { login } = useCustomer();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = new URLSearchParams(location.search).get('redirect') || '/account';
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/customer/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, password: form.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      login(data.token, data.customer);
      navigate(redirectTo);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4 py-20">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Shield size={24} className="text-orange-500" />
            <span className="font-bebas text-2xl text-white tracking-widest">WITT MACHINE</span>
          </div>
          <h1 className="font-bebas text-4xl text-white tracking-wider mb-1">SIGN IN</h1>
          <p className="font-inter text-sm text-zinc-500">Track your order. Manage your account.</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3 mb-5">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-inter text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Email Address</label>
            <input
              data-testid="login-email"
              type="email" required
              value={form.email} onChange={set('email')}
              className="w-full bg-[#1a1a1a] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 outline-none transition-colors placeholder-zinc-600"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="block font-inter text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Password</label>
            <div className="relative">
              <input
                data-testid="login-password"
                type={showPwd ? 'text' : 'password'} required
                value={form.password} onChange={set('password')}
                className="w-full bg-[#1a1a1a] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 pr-10 outline-none transition-colors placeholder-zinc-600"
                placeholder="••••••••"
              />
              <button type="button" onClick={() => setShowPwd(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors">
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button
            data-testid="login-submit"
            type="submit" disabled={loading}
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-xl tracking-widest py-4 transition-colors"
          >
            {loading ? 'SIGNING IN...' : 'SIGN IN'}
          </button>
        </form>

        <div className="mt-6 text-center space-y-3">
          <p className="font-inter text-sm text-zinc-500">
            New customer?{' '}
            <Link to="/account/register" className="text-orange-400 hover:text-orange-300 transition-colors">
              Create an account →
            </Link>
          </p>
          <p className="font-inter text-xs text-zinc-600">
            <Link to="/" className="hover:text-zinc-400 transition-colors">← Back to store</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
